import type { IntlShape } from 'react-intl';

import type { Driver } from '#domain/model/Driver.ts';
import type { Stop } from '#domain/model/Stop.ts';
import type { Tour } from '#domain/model/Tour.ts';
import type { TransportOrder } from '#domain/model/TransportOrder.ts';

export const upsellingDemoDriver: Driver = {
    id: 'premium-driver-1',
    name: 'John Doe',
};

export const getUpsellingDemoData = (intl: Pick<IntlShape, 'formatMessage'>): { upsellingDemoTour: Tour } => {
    const transportOrders: TransportOrder[] = [
        {
            id: 'premium-order-1',
            reference_id: 'OC-240315',
            secondary_reference_id: intl.formatMessage({
                id: 'intl-msg:order-communication-web.livemonitor.premium.demo.order-1.secondary-reference',
                defaultMessage: 'Lieferschein 583920',
            }),
            loading: {
                note: intl.formatMessage({
                    id: 'intl-msg:order-communication-web.livemonitor.premium.demo.order-1.loading-note',
                    defaultMessage: 'Palettenanzahl beim Beladen per Scan bestaetigen.',
                }),
            },
            goods: {
                type: 'cargo-material',
                units: [
                    {
                        id: 'test2',
                        type: 'cargo-material',
                        packageType: 'euro-pallet',
                        quantity: {
                            target: 1,
                            loaded: 3,
                        },
                        weight: {
                            target: 0,
                            loaded: 150,
                        },
                        loadingMeter: {},
                        length: 0,
                        width: 0,
                        height: 0,
                        articles: [
                            {
                                article_id: 'article-1',
                                name: intl.formatMessage({
                                    id: 'intl-msg:order-communication-web.livemonitor.premium.demo.material-1',
                                    defaultMessage: 'Material 1',
                                }),
                            },
                        ],
                        comments: {},
                    },
                ],
            },
        },
        {
            id: 'premium-order-2',
            reference_id: 'OC-240316',
            secondary_reference_id: intl.formatMessage({
                id: 'intl-msg:order-communication-web.livemonitor.premium.demo.order-2.secondary-reference',
                defaultMessage: 'Abruf 10477',
            }),
            unloading: {
                note: intl.formatMessage({
                    id: 'intl-msg:order-communication-web.livemonitor.premium.demo.order-2.unloading-note',
                    defaultMessage: 'Anlieferung an Tor 2, Leerpaletten im Tausch mitnehmen.',
                }),
            },
            goods: {
                type: 'cargo-material',
                units: [
                    {
                        id: 'test1',
                        type: 'cargo-material',
                        packageType: 'one-way-pallet',
                        quantity: {
                            target: 1,
                        },
                        weight: {
                            target: 0,
                        },
                        loadingMeter: {},
                        length: 0,
                        width: 0,
                        height: 0,
                        articles: [
                            {
                                article_id: 'article-1',
                                name: intl.formatMessage({
                                    id: 'intl-msg:order-communication-web.livemonitor.premium.demo.material-2',
                                    defaultMessage: 'Material 2',
                                }),
                            },
                        ],
                        comments: {},
                    },
                ],
            },
        },
    ];

    const stops: Stop[] = [
        {
            id: 'premium-stop-1',
            name: intl.formatMessage({
                id: 'intl-msg:order-communication-web.livemonitor.premium.demo.stop-1.name',
                defaultMessage: 'Logistikzentrum Grossbeeren',
            }),
            type: 'load',
            location: {
                street: 'Am Wall 3',
                zipCode: '14979',
                city: 'Grossbeeren',
                country: 'DE',
                latitude: '52.3607',
                longitude: '13.3097',
            },
            status: 'finished',
            startActual: '2026-03-25T06:05:00.000Z',
            startTarget: '2026-03-25T06:00:00.000Z',
            endActual: '2026-03-25T06:35:00.000Z',
            endTarget: '2026-03-25T06:30:00.000Z',
            transportOrderIds: ['premium-order-1', 'premium-order-2'],
            serviceOrderIds: [],
        },
        {
            id: 'premium-stop-2',
            name: intl.formatMessage({
                id: 'intl-msg:order-communication-web.livemonitor.premium.demo.stop-2.name',
                defaultMessage: 'Zentrallager Wustermark',
            }),
            type: 'unload',
            location: {
                street: 'An der B5 1',
                zipCode: '14641',
                city: 'Wustermark',
                country: 'DE',
                latitude: '52.5564',
                longitude: '12.9516',
            },
            status: 'open',
            startTarget: '2026-03-25T09:00:00.000Z',
            endTarget: '2026-03-25T09:30:00.000Z',
            transportOrderIds: ['premium-order-1'],
            serviceOrderIds: [],
        },
        {
            id: 'premium-stop-4',
            name: intl.formatMessage({
                id: 'intl-msg:order-communication-web.livemonitor.premium.demo.stop-3.name',
                defaultMessage: 'Markthalle Potsdam Sued',
            }),
            type: 'unload',
            location: {
                street: 'Saarmunder Strasse 44',
                zipCode: '14478',
                city: 'Potsdam',
                country: 'DE',
                latitude: '52.3512',
                longitude: '13.0898',
            },
            status: 'open',
            startTarget: '2026-03-25T15:00:00.000Z',
            endTarget: '2026-03-25T15:20:00.000Z',
            transportOrderIds: ['premium-order-2'],
            serviceOrderIds: [],
        },
    ];

    const upsellingDemoTour: Tour = {
        id: 'premium-tour-1',
        searchName: intl.formatMessage({
            id: 'intl-msg:order-communication-web.livemonitor.premium.demo.tour.name',
            defaultMessage: 'Tagestour Berlin Brandenburg',
        }),
        driverId: upsellingDemoDriver.id,
        truck: {
            id: 'premium-truck-1',
            licensePlate: 'B-OC 2401',
        },
        trailer: {
            id: 'premium-trailer-1',
            licensePlate: 'B-OC 782',
        },
        status: 'in-transit',
        progress: {
            totalStopAmount: stops.length,
            finishedStopAmount: 1,
        },
        firstStop: stops[0],
        lastStop: stops[stops.length - 1],
        note: intl.formatMessage({
            id: 'intl-msg:order-communication-web.livemonitor.premium.demo.tour.note',
            defaultMessage: 'Demodaten: regionale Tagestour.',
        }),
        seenByDriver: true,
        stops,
        transportOrders,
        serviceOrders: [],
    };

    return { upsellingDemoTour };
};
